import { addMinutes, differenceInMinutes, isAfter, subMinutes } from "date-fns";
import { toZonedTime } from "date-fns-tz";

import { isPendingBookingRequest } from "@/components/helpers/meeting/is-pending-booking-request";
import { BLUE, BRAND_PURPLE, LIGHT_PURPLE } from "@/css/constants";
import {
  ChatMeetingsQuery,
  MeetingStatus,
  SessionsQuery,
  StaffSessionsQuery,
} from "@/graphql";
import { FullMeeting, UpcomingMeeting } from "@/types/meetings";
import { ParticipantComponent } from "@/types/programs";
import { CoachingCurrentUser, CoachingCustomer, RoleView } from "@/types/users";
import { NonNullableArrayItem } from "@/types/utils";
import { getBrowserTimeZone } from "@/utils/datetime";

export type StaffMeeting = NonNullableArrayItem<
  NonNullable<StaffSessionsQuery["customer"]>["meetings"]
>;

export type SessionsMeeting = NonNullableArrayItem<
  NonNullable<SessionsQuery["currentUser"]>["meetings"]
>;

export type ChatMeeting = NonNullableArrayItem<
  ChatMeetingsQuery["chatMeetings"]
>;

const PRE_MEETING_NOTIFICATION_MINUTES = 5;
const HIGHLIGHT_COLOR_LISTS = [
  "#D8CDB2",
  "#75BCD4",
  "#EB9CAC",
  "#96AFB9",
  "#FCFCD4",
  "#C8A7CF",
  "#B4A7B7",
];

export function isRefundable(meeting: FullMeeting | ParticipantMeeting) {
  const browserTimezone = getBrowserTimeZone();
  const browserNow = new Date();
  const refundableUntil =
    meeting.refundableUntil &&
    toZonedTime(meeting.refundableUntil, browserTimezone);
  return refundableUntil && refundableUntil > browserNow;
}

type ParticipantMeeting = NonNullableArrayItem<
  ParticipantComponent["meetings"]
>;

type Meeting = (ChatMeeting | FullMeeting | ParticipantMeeting) & {
  eventAt?: FullMeeting["eventAt"];
  duration?: FullMeeting["duration"];
  canceledAt?: FullMeeting["canceledAt"];
};

export function getMeetingStatus(meeting: Meeting) {
  const browserTimezone = getBrowserTimeZone();
  const meetingStartTime = meeting.eventAt
    ? toZonedTime(meeting.eventAt, browserTimezone)
    : null;

  const duration = meeting.duration || 0;
  const currentTime = toZonedTime(new Date(), browserTimezone);

  const minutesFromNowToMeetingStart = meetingStartTime
    ? differenceInMinutes(currentTime, meetingStartTime)
    : 0;

  const isMeetingStartingSoon =
    minutesFromNowToMeetingStart > -PRE_MEETING_NOTIFICATION_MINUTES &&
    minutesFromNowToMeetingStart < duration;

  const hasMeetingEnded = minutesFromNowToMeetingStart > duration;
  const isMeetingScheduling = isPendingBookingRequest(meeting);

  if (meeting.canceledAt) {
    return MeetingStatus.Canceled;
  } else if (hasMeetingEnded) {
    return MeetingStatus.Complete;
  } else if (isMeetingStartingSoon) {
    return MeetingStatus.Soon;
  } else if (isMeetingScheduling) {
    return MeetingStatus.Scheduling;
  } else {
    return MeetingStatus.Upcoming;
  }
}

export function getMeetingColor(meetingStatus: MeetingStatus) {
  if (meetingStatus === MeetingStatus.Scheduling) {
    return "#f6ad55";
  }
  if (meetingStatus === MeetingStatus.Upcoming) {
    return BRAND_PURPLE;
  }

  return meetingStatus === MeetingStatus.Canceled ||
    meetingStatus === MeetingStatus.Complete
    ? LIGHT_PURPLE
    : meetingStatus === MeetingStatus.Soon
      ? BRAND_PURPLE
      : BLUE;
}

export function buildMeetingUrl({
  meetingId,
  customerId,
  view = "coach",
}: {
  meetingId: string;
  customerId?: string;
  view?: RoleView;
}) {
  if (customerId && view === "assist") {
    return `/assist/${customerId}/sessions/${meetingId}`;
  }
  return `/sessions/${meetingId}`;
}

export function getUserBufferSetting(
  user: CoachingCurrentUser | CoachingCustomer,
) {
  if ("agentProfile" in user) {
    return {
      beforeBuffer: user.agentProfile?.bufferBefore || 0,
      afterBuffer: user.agentProfile?.bufferAfter || 0,
    };
  }
  return {
    beforeBuffer: 0,
    afterBuffer: 0,
  };
}

interface IsOverlapMeetingProps {
  firstMeeting: FullMeeting;
  secondMeeting: FullMeeting;
  beforeBuffer?: number;
  afterBuffer?: number;
}

function isOverlapMeeting({
  firstMeeting,
  secondMeeting,
  beforeBuffer = 10,
  afterBuffer = 10,
}: IsOverlapMeetingProps) {
  const afterfirstMeetingEnding = firstMeeting.eventAt
    ? addMinutes(
        new Date(firstMeeting.eventAt),
        afterBuffer + (firstMeeting.duration || 0),
      )
    : null;
  const beforeSecondMeetingStart = secondMeeting.eventAt
    ? subMinutes(new Date(secondMeeting.eventAt), beforeBuffer)
    : null;

  return afterfirstMeetingEnding && beforeSecondMeetingStart
    ? isAfter(afterfirstMeetingEnding, beforeSecondMeetingStart)
    : false;
}

interface IsOverlapExistInMeetingListProps {
  meetingList: FullMeeting[];
  user: CoachingCurrentUser | CoachingCustomer;
}

export function isOverlapExistInMeetingList({
  meetingList,
  user,
}: IsOverlapExistInMeetingListProps) {
  const { beforeBuffer, afterBuffer } =
    "agentProfile" in user
      ? getUserBufferSetting(user)
      : { beforeBuffer: 0, afterBuffer: 0 };

  const sortedMeetingList = meetingList.sort((meetingA, meetingB) => {
    if (!meetingA.eventAt || !meetingB.eventAt) return 0;
    return meetingA.eventAt > meetingB.eventAt ? 1 : -1;
  });

  for (let index = 0; index < sortedMeetingList.length; index++) {
    if (index === 0) continue;

    if (
      isOverlapMeeting({
        firstMeeting: sortedMeetingList[index - 1],
        secondMeeting: sortedMeetingList[index],
        beforeBuffer,
        afterBuffer,
      })
    ) {
      return true;
    }
  }

  return false;
}

type MeetingWithBGColor = (UpcomingMeeting | SessionsMeeting | StaffMeeting) & {
  backgroundColor?: string;
};

export function buildOverlapMeetingList<T extends MeetingWithBGColor>({
  meetingLists,
  beforeBuffer = 10,
  afterBuffer = 10,
}: {
  meetingLists: T[];
  beforeBuffer?: number;
  afterBuffer?: number;
}): T[] {
  const sortedMeetingByEventAtAsc = meetingLists
    .map((meeting) => ({ ...meeting })) // Create new objects that are extensible
    .sort((meetingA, meetingB) => {
      if (!meetingA.eventAt || !meetingB.eventAt) return 0;
      return meetingA.eventAt > meetingB.eventAt ? 1 : -1;
    });

  let colorIndex = -1;

  sortedMeetingByEventAtAsc.forEach((meeting, index, meetingArray) => {
    if (index === 0) {
      meeting.backgroundColor = "transparent";
      return;
    }

    const beforeMeeting = meetingArray[index - 1];
    const afterMeeting = meeting;
    const isOverlap = isOverlapMeeting({
      firstMeeting: beforeMeeting,
      secondMeeting: afterMeeting,
      beforeBuffer,
      afterBuffer,
    });

    if (isOverlap) {
      if (beforeMeeting.backgroundColor === "transparent") {
        colorIndex = colorIndex > 6 ? 6 : colorIndex + 1;
        afterMeeting.backgroundColor = HIGHLIGHT_COLOR_LISTS[colorIndex];
        beforeMeeting.backgroundColor = HIGHLIGHT_COLOR_LISTS[colorIndex];
      } else {
        afterMeeting.backgroundColor = beforeMeeting.backgroundColor;
      }
    } else {
      afterMeeting.backgroundColor = "transparent";
    }
  });

  return meetingLists;
}
